<template>
  <div
    class="flex flex-col gap-6 w-full h-fit bg-white border border-grays-400 rounded py-6 pl-4 pr-9"
  >
    <!-- Header -->
    <div class="flex flex-row w-full h-fit items-center">
      <h3 class="flex-1 text-xl font-medium text-primary-700 min-h-9">
        רישום מת״צים
      </h3>
      <button-tooltip-hayadata
        :open-up="true"
        :tooltipHayadataTexts="matatzimTooltipTexts"
      />
    </div>

    <!-- Children list -->
    <div class="flex flex-col gap-4">
      <div
        v-for="(child, index) in children"
        :key="child.id || 'new-' + index"
        class="flex flex-row items-end gap-4"
      >
        <input-default
          :label="'שם'"
          :model-value="child.name"
          :disabled="!child._editing"
          @update:model-value="child.name = $event"
          class="flex-1"
        />
        <input-default
          :label="'תעודת זהות'"
          :model-value="child.taz ? String(child.taz) : ''"
          :disabled="!child._editing"
          @update:model-value="child.taz = $event"
          class="flex-1"
        />
        <div class="flex flex-row gap-2 mb-1">
          <!-- Save button (shown in edit mode) -->
          <button
            v-if="child._editing"
            class="h-fit"
            :disabled="child._saving"
            @click="saveChild(index)"
          >
            <span class="material-symbols-rounded text-lg text-primary-700"
              >check</span
            >
          </button>
          <!-- Cancel button (shown in edit mode for existing records) -->
          <button
            v-if="child._editing && child.id"
            class="h-fit"
            @click="cancelEdit(index)"
          >
            <span class="material-symbols-rounded text-lg text-grays-600"
              >close</span
            >
          </button>
          <!-- Edit button (shown in view mode) -->
          <button
            v-if="!child._editing"
            class="h-fit"
            @click="editChild(index)"
          >
            <span class="material-symbols-rounded text-lg text-grays-800"
              >edit</span
            >
          </button>
          <!-- Delete button -->
          <button
            class="h-fit"
            :disabled="child._saving"
            @click="deleteChild(index)"
          >
            <span class="material-symbols-rounded text-lg text-grays-800"
              >delete</span
            >
          </button>
        </div>
      </div>

      <!-- Empty state -->
      <p v-if="children.length === 0" class="text-grays-600 text-sm">
        לא נוספו ילדים עדיין
      </p>
    </div>

    <!-- Error message -->
    <error-block v-if="errorMessage" :error-message="errorMessage" />

    <!-- Footer: add button + info tooltip -->
    <div class="flex flex-row items-center gap-3">
      <button-text @click="addNewChild">
        <span class="material-symbols-rounded text-lg">add</span>
        הוסף ילד
      </button-text>
    </div>
  </div>
</template>

<script>
import axiosInstance from "@/helpers/axios";
import InputDefault from "@/components/Global/InputDefault.vue";
import ButtonText from "@/components/Global/ButtonText.vue";
import ErrorBlock from "@/components/Global/ErrorBlock.vue";

export default {
  name: "MatatzimCard",
  components: { InputDefault, ButtonText, ErrorBlock },
  props: {
    profileData: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      children: [],
      errorMessage: "",
      matatzimTooltipTexts: {
        title_bold: "מה זה מת״צים?",
        title_regular: "",
        description:
          "מת״צים = מתנדבים צעירים. שותפים בחוויית התנדבות בלווי ובאישור ההורים. המת״צים במשנת יוסף מקבלים הטבות מתנות ועוד פעילויות שוות. לפרטים נוספים דברו עם מנהל התחנה שלכם.",
      },
    };
  },
  watch: {
    profileData: {
      immediate: true,
      handler(data) {
        if (data && Array.isArray(data.matatzim)) {
          this.children = data.matatzim.map((child) => ({
            ...child,
            taz: child.taz != null ? String(child.taz) : "",
            _editing: false,
            _saving: false,
            _originalName: child.name,
            _originalTaz: child.taz != null ? String(child.taz) : "",
          }));
        }
      },
    },
  },
  methods: {
    addNewChild() {
      this.children.push({
        id: null,
        name: "",
        taz: "",
        _editing: true,
        _saving: false,
        _originalName: "",
        _originalTaz: "",
      });
    },
    editChild(index) {
      const child = this.children[index];
      child._originalName = child.name;
      child._originalTaz = child.taz;
      child._editing = true;
    },
    cancelEdit(index) {
      const child = this.children[index];
      child.name = child._originalName;
      child.taz = child._originalTaz;
      child._editing = false;
    },
    async saveChild(index) {
      const child = this.children[index];
      this.errorMessage = "";

      if (!child.name || !child.taz) {
        this.errorMessage = "יש למלא שם ותעודת זהות";
        return;
      }

      child._saving = true;
      try {
        if (child.id) {
          // Update existing
          const { data } = await axiosInstance.put(`/matatzim/${child.id}`, {
            name: child.name,
            taz: child.taz,
          });
          child.id = data.id ?? child.id;
        } else {
          // Create new
          const { data } = await axiosInstance.post("/matatzim", {
            name: child.name,
            taz: child.taz,
          });
          child.id = data.id;
        }
        child._editing = false;
        child._originalName = child.name;
        child._originalTaz = child.taz;
      } catch (e) {
        this.errorMessage = e.response?.data?.message || "שגיאה בשמירת הנתונים";
      } finally {
        child._saving = false;
      }
    },
    async deleteChild(index) {
      const child = this.children[index];
      this.errorMessage = "";

      // If it's a new unsaved row, just remove it locally
      if (!child.id) {
        this.children.splice(index, 1);
        return;
      }

      child._saving = true;
      try {
        await axiosInstance.delete(`/matatzim/${child.id}`);
        this.children.splice(index, 1);
      } catch (e) {
        this.errorMessage = e.response?.data?.message || "שגיאה במחיקת הנתונים";
        child._saving = false;
      }
    },
  },
};
</script>

<style scoped>
</style>
